/**
 * Nymia · Creator Page — PROTOTIPO INTERATTIVO (Scene 1-4)
 * ─────────────────────────────────────────────────────────────
 * Prototipo isolato per validare il feeling cinematografico
 * prima di implementare le 14 scene complete.
 *
 * Route: /creator-prototype (pubblica, non linkata nel menu)
 * Wireframe di riferimento: /app/memory/creator_page_wireframe.md
 *
 * Focus del prototipo:
 *  - Tipografia serif Playfair Display + Inter
 *  - Animazioni Framer Motion lente (800-1200ms, ease-out)
 *  - Blur-to-clear su immagini editorial
 *  - Fade staggered su righe di testo
 *  - Grain sottile per texture pellicola
 *  - Palette: #0A0A0A / #F5F1E8 / #D4AF37
 * ─────────────────────────────────────────────────────────────
 */
import React, { useRef } from 'react';
import { useLandingMetadata } from '../hooks/useLandingMetadata';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import LanguageSwitcher from '../components/LanguageSwitcher';

// ═══════════════════════════════════════════════════════════════
// PALETTE (definita inline per isolamento visivo dal resto app)
// ═══════════════════════════════════════════════════════════════
const COLORS = {
  black: '#0A0A0A',
  warmWhite: '#F5F1E8',
  gold: '#D4AF37',
  shadow: '#6B6B6B',
};

// ═══════════════════════════════════════════════════════════════
// IMMAGINI EDITORIAL (Unsplash · sguardi mai in camera, luce laterale)
// Sostituibili facilmente cambiando l'URL
// ═══════════════════════════════════════════════════════════════
const IMAGES = {
  // Scena 1 — workspace notturno (laptop + quaderno + tazza) — il "valore costruito"
  scene1:
    'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1920&q=90',
  // Scena 3 — canyon urbano notturno, tante finestre illuminate, mood moody+wet street
  scene3:
    'https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=1920&q=90',
  // Scena 4 — mano che stringe una tazza di caffè, luce dimmed, mood raccolto
  scene4:
    'https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&w=1600&q=90',
};

// ═══════════════════════════════════════════════════════════════
// STILI RIUTILIZZATI (Playfair, oro, spazio nero)
// ═══════════════════════════════════════════════════════════════
const serifStyle = { fontFamily: '"Playfair Display", Georgia, serif' };
const interStyle = { fontFamily: '"Inter", system-ui, sans-serif' };

// ═══════════════════════════════════════════════════════════════
// VARIANT: fade-in staggered lento per righe di testo
// ═══════════════════════════════════════════════════════════════
const lineVariants = {
  hidden: { opacity: 0, y: 24, filter: 'blur(8px)' },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 1.2,
      delay: i * 0.8,
      ease: [0.22, 0.61, 0.36, 1], // custom ease-out lento
    },
  }),
};

// Wrapper "una riga alla volta" con IntersectionObserver
const RevealLine = ({ children, index = 0, className = '', style = {} }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-15% 0px -15% 0px' });
  return (
    <motion.div
      ref={ref}
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════
// GRAIN OVERLAY — texture di pellicola sottile
// SVG noise generato inline (data URL) · pointer-events: none
// ═══════════════════════════════════════════════════════════════
const GrainOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[60] mix-blend-overlay"
    style={{
      opacity: 0.18,
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9 0 0 0 0 0.9 0 0 0 0 0.85 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
    }}
  />
);

// ═══════════════════════════════════════════════════════════════
// SCENA 1 · L'apertura
// Immagine full-screen, persona di spalle, frase serif italico
// ═══════════════════════════════════════════════════════════════
const Scene1 = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  // Parallax leggero sull'immagine
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.4]);

  return (
    <section
      ref={ref}
      data-testid="scene-1"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
      style={{ backgroundColor: COLORS.black }}
    >
      {/* Immagine di sfondo con blur-to-clear + parallax */}
      <motion.div
        initial={{ filter: 'blur(20px)', opacity: 0 }}
        animate={{ filter: 'blur(0px)', opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ y, opacity: imageOpacity }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.scene1}
          alt=""
          className="h-full w-full object-cover"
          style={{ filter: 'brightness(0.55) contrast(1.05)' }}
        />
        {/* Vignette per far emergere la scritta al centro */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.65) 55%, rgba(0,0,0,0.9) 100%)',
          }}
        />
      </motion.div>

      {/* Testo centrato */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.8, delay: 1.2, ease: [0.22, 0.61, 0.36, 1] }}
        className="relative z-10 px-8 text-center"
      >
        <p
          className="text-4xl leading-tight sm:text-5xl md:text-6xl lg:text-7xl"
          style={{
            ...serifStyle,
            fontStyle: 'italic',
            fontWeight: 400,
            color: COLORS.warmWhite,
            letterSpacing: '-0.02em',
            textShadow: '0 2px 24px rgba(0,0,0,0.6)',
          }}
        >
          Per anni hai costruito valore.
        </p>
      </motion.div>

      {/* Scroll hint sottile, appare dopo 3s */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ duration: 1.5, delay: 3.5 }}
        className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2"
      >
        <div
          className="text-xs uppercase tracking-[0.3em]"
          style={{ ...interStyle, color: COLORS.shadow }}
        >
          scorri
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
          className="mx-auto mt-3 h-8 w-px"
          style={{ backgroundColor: COLORS.shadow }}
        />
      </motion.div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════
// SCENA 2 · Il paradosso, senza nominarlo
// Sfondo nero puro, righe staggered, poi la domanda sospesa
// ═══════════════════════════════════════════════════════════════
const Scene2 = () => (
  <section
    data-testid="scene-2"
    className="relative flex min-h-screen items-center justify-center px-8 py-32"
    style={{ backgroundColor: COLORS.black }}
  >
    <div className="mx-auto max-w-3xl">
      {/* Prime 3 righe — fade staggered */}
      <div className="space-y-8 md:space-y-12">
        <RevealLine index={0}>
          <p
            className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
            style={{
              ...serifStyle,
              fontWeight: 400,
              color: COLORS.warmWhite,
              letterSpacing: '-0.01em',
            }}
          >
            Ogni contenuto che pubblichi ha un valore.
          </p>
        </RevealLine>
        <RevealLine index={1}>
          <p
            className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
            style={{
              ...serifStyle,
              fontWeight: 400,
              color: COLORS.warmWhite,
              letterSpacing: '-0.01em',
            }}
          >
            Ogni ora che dedichi ha un valore.
          </p>
        </RevealLine>
        <RevealLine index={2}>
          <p
            className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
            style={{
              ...serifStyle,
              fontWeight: 400,
              color: COLORS.warmWhite,
              letterSpacing: '-0.01em',
            }}
          >
            Ogni conversazione che avvii ha un valore.
          </p>
        </RevealLine>
      </div>

      {/* Separatore lento — linea oro che si estende */}
      <RevealLine index={3} className="my-20 flex justify-center">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: '4rem' }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
          className="h-px"
          style={{ backgroundColor: COLORS.gold, opacity: 0.7 }}
        />
      </RevealLine>

      {/* Domanda sospesa in italico */}
      <RevealLine index={4}>
        <p
          className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
          style={{
            ...serifStyle,
            fontStyle: 'italic',
            fontWeight: 400,
            color: COLORS.warmWhite,
            letterSpacing: '-0.01em',
          }}
        >
          Ma dove finisce, quel valore?
        </p>
      </RevealLine>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════
// SCENA 3 · Il riconoscimento
// Città notturna dall'alto, testo, poi carezza in oro
// ═══════════════════════════════════════════════════════════════
const Scene3 = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section
      ref={ref}
      data-testid="scene-3"
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-8 py-32"
      style={{ backgroundColor: COLORS.black }}
    >
      {/* Immagine di sfondo — città di notte con parallax */}
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src={IMAGES.scene3}
          alt=""
          className="h-full w-full object-cover"
          initial={{ filter: 'blur(24px)', opacity: 0 }}
          whileInView={{ filter: 'blur(0px)', opacity: 1 }}
          viewport={{ once: true, margin: '-20%' }}
          transition={{ duration: 1.8, ease: [0.22, 0.61, 0.36, 1] }}
          style={{ filter: 'brightness(0.35) contrast(1.1) saturate(0.85)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.5) 40%, rgba(10,10,10,0.5) 60%, rgba(10,10,10,0.9) 100%)',
          }}
        />
      </motion.div>

      {/* Testo */}
      <div className="relative z-10 mx-auto max-w-3xl">
        <div className="space-y-6 md:space-y-8">
          <RevealLine index={0}>
            <p
              className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
              style={{
                ...serifStyle,
                fontWeight: 400,
                color: COLORS.warmWhite,
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.7)',
              }}
            >
              Ogni giorno milioni di persone come te lavorano.
            </p>
          </RevealLine>
          <RevealLine index={1}>
            <p
              className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
              style={{
                ...serifStyle,
                fontWeight: 400,
                color: COLORS.warmWhite,
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.7)',
              }}
            >
              Ogni giorno costruiscono qualcosa che alimenta una timeline di
              qualcun altro.
            </p>
          </RevealLine>
        </div>

        {/* Pausa visiva */}
        <div className="h-16 md:h-24" />

        {/* Carezza in oro italico */}
        <RevealLine index={2}>
          <p
            className="text-3xl leading-relaxed sm:text-4xl md:text-5xl"
            style={{
              ...serifStyle,
              fontStyle: 'italic',
              fontWeight: 500,
              color: COLORS.gold,
              letterSpacing: '-0.01em',
              textShadow: '0 2px 20px rgba(0,0,0,0.7)',
            }}
          >
            Non è colpa tua.
          </p>
        </RevealLine>
        <RevealLine index={3} className="mt-4">
          <p
            className="text-2xl leading-relaxed sm:text-3xl md:text-4xl"
            style={{
              ...serifStyle,
              fontStyle: 'italic',
              fontWeight: 400,
              color: COLORS.warmWhite,
              opacity: 0.85,
              letterSpacing: '-0.01em',
              textShadow: '0 2px 20px rgba(0,0,0,0.7)',
            }}
          >
            È come sono stati progettati questi spazi.
          </p>
        </RevealLine>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════
// SCENA 4 · Il momento della soglia
// Dettaglio mani/tazza, singola frase enorme in italico oro
// ═══════════════════════════════════════════════════════════════
const Scene4 = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const imgOpacity = useTransform(
    scrollYProgress,
    [0, 0.4, 0.7, 1],
    [0, 0.55, 0.55, 0.2]
  );

  return (
    <section
      ref={ref}
      data-testid="scene-4"
      className="relative flex min-h-screen items-center justify-center overflow-hidden px-8 py-32"
      style={{ backgroundColor: COLORS.black }}
    >
      {/* Immagine — molto piccola e centrata, come un dettaglio in penombra */}
      <motion.div
        initial={{ opacity: 0, filter: 'blur(30px)' }}
        whileInView={{ opacity: 1, filter: 'blur(0px)' }}
        viewport={{ once: true, margin: '-25%' }}
        transition={{ duration: 2.0, ease: [0.22, 0.61, 0.36, 1] }}
        style={{ scale: imgScale, opacity: imgOpacity }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.scene4}
          alt=""
          className="h-full w-full object-cover"
          style={{ filter: 'brightness(0.35) contrast(1.15) saturate(0.75)' }}
        />
        {/* Vignette forte per lasciare emergere solo il testo */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.75) 45%, rgba(0,0,0,0.95) 100%)',
          }}
        />
      </motion.div>

      {/* Frase — italic gold, gigantesca */}
      <div className="relative z-10 mx-auto max-w-4xl text-center">
        <RevealLine index={0}>
          <p
            className="text-4xl leading-tight sm:text-5xl md:text-6xl lg:text-7xl"
            style={{
              ...serifStyle,
              fontStyle: 'italic',
              fontWeight: 500,
              color: COLORS.gold,
              letterSpacing: '-0.02em',
              textShadow:
                '0 2px 40px rgba(0,0,0,0.9), 0 0 60px rgba(212,175,55,0.15)',
            }}
          >
            Immagina se quel valore
          </p>
        </RevealLine>
        <RevealLine index={1} className="mt-6">
          <p
            className="text-4xl leading-tight sm:text-5xl md:text-6xl lg:text-7xl"
            style={{
              ...serifStyle,
              fontStyle: 'italic',
              fontWeight: 500,
              color: COLORS.gold,
              letterSpacing: '-0.02em',
              textShadow:
                '0 2px 40px rgba(0,0,0,0.9), 0 0 60px rgba(212,175,55,0.15)',
            }}
          >
            tornasse a te.
          </p>
        </RevealLine>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════
// CHIUSURA PROTOTIPO — "titolo di coda" per indicare fine test
// ═══════════════════════════════════════════════════════════════
const PrototypeEnd = () => (
  <section
    data-testid="prototype-end"
    className="flex min-h-[60vh] items-center justify-center px-8 py-24"
    style={{ backgroundColor: COLORS.black }}
  >
    <div className="text-center">
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.4 }}
      >
        <p
          className="text-sm uppercase tracking-[0.4em]"
          style={{ ...interStyle, color: COLORS.shadow }}
        >
          — fine prototipo —
        </p>
        <p
          className="mt-8 text-lg leading-relaxed"
          style={{
            ...serifStyle,
            fontStyle: 'italic',
            fontWeight: 400,
            color: COLORS.warmWhite,
            opacity: 0.55,
          }}
        >
          Le prossime 10 scene sono in attesa della tua approvazione.
        </p>
      </motion.div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════
// PAGINA COMPLETA
// ═══════════════════════════════════════════════════════════════
export default function CreatorPrototypePage() {
  useLandingMetadata('creator');
  return (
    <div
      data-testid="creator-prototype-page"
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ backgroundColor: COLORS.black, color: COLORS.warmWhite }}
    >
      {/* Grain overlay globale */}
      <GrainOverlay />

      {/* Chrome minimale: logo Nymia + language switcher, quasi invisibili */}
      <header
        className="pointer-events-none fixed inset-x-0 top-0 z-50 flex items-center justify-between px-6 py-5 md:px-10 md:py-6"
        data-testid="prototype-header"
      >
        <div
          className="pointer-events-auto"
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: '1.5rem',
            fontWeight: 500,
            letterSpacing: '0.08em',
            color: COLORS.warmWhite,
            opacity: 0.4,
          }}
        >
          Nymia
        </div>
        <div className="pointer-events-auto" style={{ opacity: 0.5 }}>
          <LanguageSwitcher
            variant="compact"
            testId="prototype-lang-switcher"
          />
        </div>
      </header>

      {/* Le 4 scene */}
      <main>
        <Scene1 />
        <Scene2 />
        <Scene3 />
        <Scene4 />
        <PrototypeEnd />
      </main>
    </div>
  );
}
